import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { geoMercator, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import type { Topology } from 'topojson-specification';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import type { StateDetail, StateDetailSchool, StateDetailTerritory } from '../../types';
import { STATE_BOUNDS, ALASKA_CROPPED_BOUNDS } from '../../constants/stateBounds';
import { getCitiesForState } from '../../constants/majorCities';
import SchoolTooltip from './SchoolTooltip';
import TerritoryHoverCard from './TerritoryHoverCard';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const STATE_TO_FIPS: Record<string, string> = {
  'AL': '01', 'AK': '02', 'AZ': '04', 'AR': '05', 'CA': '06',
  'CO': '08', 'CT': '09', 'DE': '10', 'DC': '11', 'FL': '12',
  'GA': '13', 'HI': '15', 'ID': '16', 'IL': '17', 'IN': '18',
  'IA': '19', 'KS': '20', 'KY': '21', 'LA': '22', 'ME': '23',
  'MD': '24', 'MA': '25', 'MI': '26', 'MN': '27', 'MS': '28',
  'MO': '29', 'MT': '30', 'NE': '31', 'NV': '32', 'NH': '33',
  'NJ': '34', 'NM': '35', 'NY': '36', 'NC': '37', 'ND': '38',
  'OH': '39', 'OK': '40', 'OR': '41', 'PA': '42', 'RI': '44',
  'SC': '45', 'SD': '46', 'TN': '47', 'TX': '48', 'UT': '49',
  'VT': '50', 'VA': '51', 'WA': '53', 'WV': '54', 'WI': '55',
  'WY': '56',
};

const SCHOOL_COLORS = {
  blast: '#22c55e',
  competitor: '#ef4444',
  open: '#9ca3af',
} as const;

// Distinct territory fill colors — visually separated, semi-transparent friendly
const TERRITORY_COLORS = [
  '#6366f1', // indigo
  '#f59e0b', // amber
  '#10b981', // emerald
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
  '#14b8a6', // teal
  '#84cc16', // lime
  '#a855f7', // purple
  '#e11d48', // rose
];

interface StateMapProps {
  stateCode: string;
  stateDetail: StateDetail;
  zctaData: GeoJSON.FeatureCollection | null;
  showTerritories: boolean;
  showHighways: boolean;
  showCities: boolean;
}

interface TooltipState {
  type: 'school' | 'territory';
  x: number;
  y: number;
  school?: StateDetailSchool;
  territory?: StateDetailTerritory;
}

const MAP_WIDTH = 800;
const MAP_HEIGHT = 600;
const MAP_PADDING = 40;
const MIN_ZOOM = 1;
const MAX_ZOOM = 40;
const ZOOM_STEP = 1.4;

export default function StateMap({ stateCode, stateDetail, zctaData, showTerritories, showHighways, showCities }: StateMapProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [stateGeo, setStateGeo] = useState<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Zoom & pan state — track viewBox origin directly
  const [zoom, setZoom] = useState(1);
  const [viewOrigin, setViewOrigin] = useState({ x: 0, y: 0 }); // top-left of viewBox
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ clientX: 0, clientY: 0, originX: 0, originY: 0 });
  const tooltipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load state outline from US Atlas
  useEffect(() => {
    let cancelled = false;
    fetch(GEO_URL)
      .then(r => r.json())
      .then((topo: Topology) => {
        if (cancelled) return;
        const states = feature(topo, topo.objects.states) as GeoJSON.FeatureCollection;
        const fips = STATE_TO_FIPS[stateCode];
        const stateFeature = states.features.find(
          (f: any) => f.id?.toString().padStart(2, '0') === fips
        );
        setStateGeo(stateFeature);
      })
      .catch(err => console.error('Failed to load state outline:', err));
    return () => { cancelled = true; };
  }, [stateCode]);

  // Load highway data
  const [highwayData, setHighwayData] = useState<GeoJSON.FeatureCollection | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/data/highways.json')
      .then(r => r.json())
      .then((data: GeoJSON.FeatureCollection) => {
        if (cancelled) return;
        setHighwayData(data);
      })
      .catch(() => {}); // highways are optional
    return () => { cancelled = true; };
  }, []);

  // Cities for this state
  const cities = useMemo(() => getCitiesForState(stateCode), [stateCode]);

  // Build projection fitted to the state's bounding box
  const projection = useMemo(() => {
    const bounds = stateCode === 'AK' ? ALASKA_CROPPED_BOUNDS : STATE_BOUNDS[stateCode];
    if (!bounds) return null;

    const [minLng, minLat, maxLng, maxLat] = bounds;
    const proj = geoMercator();

    proj.fitExtent(
      [[MAP_PADDING, MAP_PADDING], [MAP_WIDTH - MAP_PADDING, MAP_HEIGHT - MAP_PADDING]],
      {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [[
            [minLng, minLat],
            [minLng, maxLat],
            [maxLng, maxLat],
            [maxLng, minLat],
            [minLng, minLat],
          ]],
        },
      }
    );

    return proj;
  }, [stateCode]);

  const pathGen = useMemo(() => {
    if (!projection) return null;
    return geoPath().projection(projection);
  }, [projection]);

  // Build a prefix → territory lookup for coloring ZCTA zones
  const prefixToTerritory = useMemo(() => {
    const map: Record<string, { territory: StateDetailTerritory; colorIndex: number }> = {};
    stateDetail.territories.forEach((t, i) => {
      t.zipPrefixes.forEach(prefix => {
        map[prefix] = { territory: t, colorIndex: i };
      });
    });
    return map;
  }, [stateDetail.territories]);

  // Assign every ZCTA feature to a territory (like the national map colors states by region)
  // Directly assigned ZCTAs get their territory. Unassigned ones get the geographically nearest.
  const zctaTerritoryMap = useMemo(() => {
    if (!zctaData) return new Map<number, { territory: StateDetailTerritory; color: string }>();

    // Compute centroid of a geometry
    const centroid = (geom: any): [number, number] => {
      const pts: number[][] = [];
      const collect = (coords: any) => {
        if (typeof coords[0] === 'number') { pts.push(coords); return; }
        for (const c of coords) collect(c);
      };
      collect(geom.coordinates);
      if (pts.length === 0) return [0, 0];
      const sx = pts.reduce((s, p) => s + p[0], 0);
      const sy = pts.reduce((s, p) => s + p[1], 0);
      return [sx / pts.length, sy / pts.length];
    };

    // Pre-compute territory centroids (average of all their ZCTA centroids)
    const territoryCentroids: [number, number][] = stateDetail.territories.map((t) => {
      const prefixes = new Set(t.zipPrefixes);
      const tCentroids: [number, number][] = [];
      zctaData.features.forEach(f => {
        if (f.properties?.prefix && prefixes.has(f.properties.prefix)) {
          tCentroids.push(centroid(f.geometry));
        }
      });
      if (tCentroids.length === 0) return [0, 0] as [number, number];
      return [
        tCentroids.reduce((s, c) => s + c[0], 0) / tCentroids.length,
        tCentroids.reduce((s, c) => s + c[1], 0) / tCentroids.length,
      ] as [number, number];
    });

    const result = new Map<number, { territory: StateDetailTerritory; color: string }>();

    zctaData.features.forEach((f, i) => {
      const prefix = f.properties?.prefix;

      // Check if directly assigned
      if (prefix && prefixToTerritory[prefix]) {
        const match = prefixToTerritory[prefix];
        result.set(i, {
          territory: match.territory,
          color: TERRITORY_COLORS[match.colorIndex % TERRITORY_COLORS.length],
        });
        return;
      }

      // Unassigned — find geographically nearest territory
      const c = centroid(f.geometry);
      let bestIdx = -1;
      let bestDist = Infinity;
      territoryCentroids.forEach((tc, tIdx) => {
        const dx = c[0] - tc[0];
        const dy = c[1] - tc[1];
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = tIdx;
        }
      });

      if (bestIdx >= 0) {
        result.set(i, {
          territory: stateDetail.territories[bestIdx],
          color: TERRITORY_COLORS[bestIdx % TERRITORY_COLORS.length],
        });
      }
    });

    return result;
  }, [zctaData, stateDetail.territories, prefixToTerritory]);

  // Convert screen pixel position to viewBox coordinates
  const screenToViewBox = useCallback((clientX: number, clientY: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const vbWidth = MAP_WIDTH / zoom;
    const vbHeight = MAP_HEIGHT / zoom;
    return {
      x: viewOrigin.x + (clientX - rect.left) / rect.width * vbWidth,
      y: viewOrigin.y + (clientY - rect.top) / rect.height * vbHeight,
    };
  }, [zoom, viewOrigin]);

  // Zoom centered on a viewBox point (keeps that point stationary on screen)
  const zoomAt = useCallback((factor: number, clientX?: number, clientY?: number) => {
    setZoom(prev => {
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev * factor));
      const actualFactor = newZoom / prev;
      if (actualFactor === 1) return prev;

      if (clientX !== undefined && clientY !== undefined) {
        const rect = svgRef.current?.getBoundingClientRect();
        if (rect) {
          // Where is the cursor in the current viewBox?
          const oldVbW = MAP_WIDTH / prev;
          const oldVbH = MAP_HEIGHT / prev;
          const fracX = (clientX - rect.left) / rect.width;
          const fracY = (clientY - rect.top) / rect.height;
          const cursorVbX = viewOrigin.x + fracX * oldVbW;
          const cursorVbY = viewOrigin.y + fracY * oldVbH;

          // New viewBox size
          const newVbW = MAP_WIDTH / newZoom;
          const newVbH = MAP_HEIGHT / newZoom;

          // Set origin so cursor stays at the same screen fraction
          setViewOrigin({
            x: cursorVbX - fracX * newVbW,
            y: cursorVbY - fracY * newVbH,
          });
        }
      } else {
        // Zoom from center
        const oldVbW = MAP_WIDTH / prev;
        const oldVbH = MAP_HEIGHT / prev;
        const newVbW = MAP_WIDTH / newZoom;
        const newVbH = MAP_HEIGHT / newZoom;
        setViewOrigin(o => ({
          x: o.x + (oldVbW - newVbW) / 2,
          y: o.y + (oldVbH - newVbH) / 2,
        }));
      }

      return newZoom;
    });
  }, [viewOrigin]);

  const resetView = useCallback(() => {
    setZoom(1);
    setViewOrigin({ x: 0, y: 0 });
  }, []);

  // Wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
    zoomAt(factor, e.clientX, e.clientY);
  }, [zoomAt]);

  // Pan handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    panStart.current = { clientX: e.clientX, clientY: e.clientY, originX: viewOrigin.x, originY: viewOrigin.y };
  }, [viewOrigin]);

  const handleMouseMoveGlobal = useCallback((e: React.MouseEvent) => {
    if (!isPanning) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const vbWidth = MAP_WIDTH / zoom;
    const vbHeight = MAP_HEIGHT / zoom;
    // Dragging right → viewBox origin moves left (negative)
    const dx = ((e.clientX - panStart.current.clientX) / rect.width) * vbWidth;
    const dy = ((e.clientY - panStart.current.clientY) / rect.height) * vbHeight;
    setViewOrigin({ x: panStart.current.originX - dx, y: panStart.current.originY - dy });
  }, [isPanning, zoom]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Tooltip with debounce to prevent bouncing
  const getRelativePos = useCallback((e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    return {
      x: e.clientX - (rect?.left ?? 0),
      y: e.clientY - (rect?.top ?? 0),
    };
  }, []);

  const showTooltip = useCallback((state: TooltipState) => {
    if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
    setTooltip(state);
  }, []);

  const handleSchoolHover = useCallback((e: React.MouseEvent, school: StateDetailSchool) => {
    if (isPanning) return;
    const pos = getRelativePos(e);
    showTooltip({ type: 'school', ...pos, school });
  }, [getRelativePos, showTooltip, isPanning]);

  const handleZoneHover = useCallback((e: React.MouseEvent, territory: StateDetailTerritory) => {
    if (isPanning) return;
    const pos = getRelativePos(e);
    showTooltip({ type: 'territory', ...pos, territory });
  }, [getRelativePos, showTooltip, isPanning]);

  const handleTooltipMove = useCallback((e: React.MouseEvent) => {
    if (!tooltip || isPanning) return;
    const pos = getRelativePos(e);
    setTooltip(prev => prev ? { ...prev, ...pos } : null);
  }, [tooltip, getRelativePos, isPanning]);

  const handleMouseLeave = useCallback(() => {
    if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
    tooltipTimer.current = setTimeout(() => setTooltip(null), 50);
  }, []);

  if (!projection || !pathGen) {
    return <div className="h-96 flex items-center justify-center text-gray-500 dark:text-gray-400">Unable to render map</div>;
  }

  // Compute viewBox based on zoom + viewOrigin
  const vbWidth = MAP_WIDTH / zoom;
  const vbHeight = MAP_HEIGHT / zoom;
  const vbX = viewOrigin.x;
  const vbY = viewOrigin.y;

  // Scale dot size inversely with zoom so they stay usable
  const dotRadius = 3 / zoom;
  const dotStroke = 0.5 / zoom;

  return (
    <div ref={containerRef} className="relative bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
      {/* Zoom controls */}
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-1">
        <button
          onClick={() => zoomAt(ZOOM_STEP, MAP_WIDTH / 2, MAP_HEIGHT / 2)}
          className="p-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          title="Zoom in"
        >
          <ZoomIn className="h-4 w-4 text-gray-600 dark:text-gray-300" />
        </button>
        <button
          onClick={() => zoomAt(1 / ZOOM_STEP, MAP_WIDTH / 2, MAP_HEIGHT / 2)}
          className="p-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          title="Zoom out"
        >
          <ZoomOut className="h-4 w-4 text-gray-600 dark:text-gray-300" />
        </button>
        <button
          onClick={resetView}
          className="p-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          title="Reset view"
        >
          <Maximize2 className="h-4 w-4 text-gray-600 dark:text-gray-300" />
        </button>
        {zoom > 1 && (
          <div className="text-center text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {zoom.toFixed(1)}x
          </div>
        )}
      </div>

      <svg
        ref={svgRef}
        viewBox={`${vbX} ${vbY} ${vbWidth} ${vbHeight}`}
        className="w-full h-auto select-none"
        style={{ maxHeight: '70vh', cursor: isPanning ? 'grabbing' : 'grab' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={(e) => { handleMouseMoveGlobal(e); handleTooltipMove(e); }}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { handleMouseUp(); handleMouseLeave(); }}
      >
        {/* Clip path for state boundary — clips highways to within the state */}
        {stateGeo && (
          <defs>
            <clipPath id="state-clip">
              <path d={pathGen(stateGeo) ?? ''} />
            </clipPath>
          </defs>
        )}

        {/* Layer 1: State outline background */}
        {stateGeo && (
          <path
            d={pathGen(stateGeo) ?? ''}
            strokeWidth={1.5 / zoom}
            className="fill-gray-100 stroke-gray-300 dark:fill-gray-700 dark:stroke-gray-600"
          />
        )}

        {/* Layer 2a: Territory color fills (toggleable) */}
        {showTerritories && zctaData && zctaData.features.map((feature, i) => {
          const match = zctaTerritoryMap.get(i);
          if (!match) return null;
          return (
            <path
              key={`zcta-fill-${i}`}
              d={pathGen!(feature as any) ?? ''}
              fill={match.color}
              fillOpacity={0.25}
              stroke="none"
              onMouseEnter={(e) => handleZoneHover(e, match.territory)}
              onMouseMove={handleTooltipMove}
              onMouseLeave={handleMouseLeave}
              style={{ cursor: 'pointer' }}
            />
          );
        })}

        {/* Layer 2b: Faint ZIP hairlines (always visible) */}
        {zctaData && zctaData.features.map((feature, i) => (
          <path
            key={`zcta-line-${i}`}
            d={pathGen!(feature as any) ?? ''}
            fill="none"
            stroke="white"
            strokeWidth={0.3 / zoom}
            strokeOpacity={0.15}
            pointerEvents="none"
          />
        ))}

        {/* Layer 3: Highways (clipped to state) */}
        {showHighways && highwayData && pathGen && (
          <g clipPath={stateGeo ? 'url(#state-clip)' : undefined}>
            {highwayData.features.map((feature, i) => {
              const d = pathGen(feature as any);
              if (!d) return null;
              const name = (feature.properties as any)?.name;
              const label = name ? `I-${name}` : '';
              return (
                <g key={`hwy-${i}`}>
                  {/* Highway line */}
                  <path
                    d={d}
                    fill="none"
                    stroke="#fbbf24"
                    strokeWidth={1.8 / zoom}
                    strokeOpacity={0.7}
                    strokeLinecap="round"
                    pointerEvents="none"
                  />
                  {/* Highway label — rendered along the path */}
                  {label && (
                    <>
                      <defs>
                        <path id={`hwy-path-${i}`} d={d} />
                      </defs>
                      <text
                        fontSize={10 / zoom}
                        fill="#fbbf24"
                        fillOpacity={0.9}
                        stroke="#1f2937"
                        strokeWidth={2 / zoom}
                        strokeLinejoin="round"
                        paintOrder="stroke"
                        pointerEvents="none"
                        className="select-none"
                        style={{ fontFamily: 'system-ui, sans-serif', fontWeight: 600 }}
                      >
                        <textPath href={`#hwy-path-${i}`} startOffset="30%">
                          {label}
                        </textPath>
                      </text>
                    </>
                  )}
                </g>
              );
            })}
          </g>
        )}

        {/* Layer 4: School dots */}
        {stateDetail.schools.map(school => {
          const point = projection([school.lng, school.lat]);
          if (!point) return null;
          const [cx, cy] = point;

          return (
            <circle
              key={school.id}
              cx={cx}
              cy={cy}
              r={dotRadius}
              fill={SCHOOL_COLORS[school.status]}
              stroke="#ffffff"
              strokeWidth={dotStroke}
              opacity={0.85}
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => { e.stopPropagation(); handleSchoolHover(e, school); }}
              onMouseMove={(e) => { e.stopPropagation(); handleTooltipMove(e); }}
              onMouseLeave={handleMouseLeave}
            />
          );
        })}

        {/* Layer 5: City labels ON TOP of school dots */}
        {showCities && projection && cities.map((city, i) => {
          const point = projection([city.lng, city.lat]);
          if (!point) return null;
          const [cx, cy] = point;
          const fontSize = (city.pop > 500000 ? 16 : city.pop > 200000 ? 13 : 10) / zoom;
          const dotR = 2.5 / zoom;
          const offset = 5 / zoom;
          const strokeW = 3.5 / zoom;
          if (zoom < 2 && city.pop < 100000) return null;
          if (zoom < 1.3 && city.pop < 200000) return null;
          return (
            <g key={`city-${i}`} pointerEvents="none">
              <circle cx={cx} cy={cy} r={dotR} fill="#ffffff" stroke="#374151" strokeWidth={0.5 / zoom} />
              <text
                x={cx + offset}
                y={cy + fontSize * 0.35}
                fontSize={fontSize}
                fill="#e5e7eb"
                stroke="#1f2937"
                strokeWidth={strokeW}
                strokeLinejoin="round"
                paintOrder="stroke"
                className="select-none"
                style={{ fontFamily: 'system-ui, sans-serif', fontWeight: city.pop > 500000 ? 700 : 500 }}
              >
                {city.name}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Tooltips */}
      {tooltip?.type === 'school' && tooltip.school && (
        <SchoolTooltip position={{ x: tooltip.x, y: tooltip.y }} school={tooltip.school} />
      )}
      {tooltip?.type === 'territory' && tooltip.territory && (
        <TerritoryHoverCard position={{ x: tooltip.x, y: tooltip.y }} territory={tooltip.territory} />
      )}

      {/* Zoom hint */}
      {zoom === 1 && (
        <div className="absolute bottom-3 left-3 text-xs text-gray-400 dark:text-gray-500">
          Scroll to zoom, drag to pan
        </div>
      )}
    </div>
  );
}
